<script setup>
import { Loader } from "@googlemaps/js-api-loader";
import { onMounted, ref, watch } from "vue";

const props = defineProps({
	addressData: {
		type: Object,
		default: () => ({}),
	},
	inputId: {
		type: String,
		required: true,
	},
	isValid: {
		type: Boolean,
		required: true,
	},
});

const emit = defineEmits(["update:addressData"]);

// Internal state
const internalAddressData = ref(props.addressData);
// Incoming
watch(
	() => props.addressData,
	(newValue) => {
		internalAddressData.value = newValue;
	},
);
// Outgoing
watch(internalAddressData, (newValue) => {
	emit("update:addressData", newValue);
});

const input = ref(null);

const loader = new Loader({
	apiKey: window.GOOGLE_MAPS_API_KEY,
	libraries: ["places"],
	language: "en",
});

const initAddressSearch = async () => {
	try {
		const google = await loader.load();

		const autocomplete = new google.maps.places.Autocomplete(input.value, {
			fields: ["address_components", "geometry.location", "formatted_address"],
		});

		autocomplete.addListener("place_changed", () => {
			internalAddressData.value = parseAddress(autocomplete.getPlace());
		});
	} catch (error) {
		console.error("Error loading Google Maps:", error);
	}
};

const parseAddress = (address) => {
	if (!address.address_components) {
		return null;
	}

	const findComponent = (type) => {
		const component = address.address_components.find((c) =>
			c.types.includes(type),
		);
		return component ? component.long_name : "";
	};

	return {
		latitude: address.geometry.location.lat(),
		longitude: address.geometry.location.lng(),
		fullAddress: address.formatted_address,
		number: findComponent("street_number"),
		address: findComponent("route"),
		city: findComponent("locality"),
		postcode: findComponent("postal_code"),
		county: findComponent("administrative_area_level_2"),
		state: findComponent("administrative_area_level_1"),
		country: findComponent("country"),
	};
};

onMounted(() => {
	initAddressSearch();
});
</script>

<template>
	<input
		class="form-control map-places-autocomplete search-location"
		:class="{ 'is-invalid': !isValid }"
		type="text"
		placeholder="Search address..."
		:id="inputId"
		ref="input"
		:value="internalAddressData?.fullAddress"
		required
	/>
</template>
